import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VideocamIcon from "@mui/icons-material/Videocam";
import { theme } from "../../theme/colors";
import { canEditEvent, isRecurringEvent, formatRecurrenceText } from "../../utils/eventEditAccess";
import { fetchEventById } from "../../utils/eventApi";

function formatEventDate(start, end) {
  if (!start) return "";
  const s = new Date(start);
  const base = s.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  if (!end) return base;
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) {
    return `${base} - ${e.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  return `${base} - ${e.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function formatLocation(event) {
  if (event.is_virtual) return "Virtual Event";
  return [event.location_name, event.city, event.state].filter(Boolean).join(", ") || "Location TBD";
}

function EventManageCard({ event }) {
  const navigate = useNavigate();
  const isPast = !canEditEvent(event);
  const [duplicating, setDuplicating] = useState(false);

  const handleDuplicate = async () => {
    if (duplicating) return;
    setDuplicating(true);
    try {
      const fullEvent = await fetchEventById(event.id);
      navigate("/events/create", { state: { duplicateFrom: fullEvent } });
    } catch {
      navigate("/events/create", { state: { duplicateFrom: event } });
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(4,120,87,0.12)",
        borderRadius: 3,
        p: 2,
        backgroundColor: "#fff",
        boxShadow: "0 4px 18px rgba(15,23,42,0.05)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {event.event_type_name && (
              <Chip
                size="small"
                label={event.event_type_name}
                sx={{
                  backgroundColor: `${theme.primary}12`,
                  color: theme.primary,
                  fontWeight: 700,
                }}
              />
            )}
            {event.pending_volunteer_count > 0 && (
              <Chip
                size="small"
                label={`${event.pending_volunteer_count} pending volunteer${event.pending_volunteer_count === 1 ? '' : 's'}`}
                sx={{ 
                  backgroundColor: theme.warningBadgeBg || '#fffbeb',
                  color: theme.warningBadgeText || '#b45309',
                  border: `1px solid ${theme.warningBadgeBorder || '#fde68a'}`,
                  fontWeight: 700 
                }}
              />
            )}
            {isRecurringEvent(event) && (
              <Chip size="small" variant="outlined" label={formatRecurrenceText(event)} sx={{ fontWeight: 600 }} />
            )}
          </Stack>

          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.75 }}>
            {event.title}
          </Typography>

          <Stack spacing={0.75} sx={{ color: "#334155" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: theme.primary }} />
              <Typography variant="body2">{formatEventDate(event.start_datetime, event.end_datetime)}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {event.is_virtual ? (
                <VideocamIcon sx={{ fontSize: 16, color: theme.primary }} />
              ) : (
                <LocationOnIcon sx={{ fontSize: 16, color: theme.primary }} />
              )}
              <Typography variant="body2">{formatLocation(event)}</Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          {!isPast && (
            <Button
              component={Link}
              to={`/events/${event.id}/edit`}
              variant="text"
              startIcon={<EditIcon sx={{ fontSize: 16, color: "#0f172a" }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#0f172a",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                minWidth: "auto",
                minHeight: 38,
                px: 0.75,
                "&:hover": {
                  bgcolor: "transparent",
                  color: "#0f172a",
                  textDecoration: "underline",
                },
              }}
            >
              Edit
            </Button>
          )}
          {isPast && (
            <Button
              onClick={handleDuplicate}
              disabled={duplicating}
              variant="text"
              startIcon={
                duplicating ? (
                  <CircularProgress size={14} sx={{ color: theme.primary }} />
                ) : (
                  <ContentCopyOutlinedIcon sx={{ fontSize: 16, color: theme.primary }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: theme.primary,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                minWidth: "auto",
                minHeight: 38,
                px: 0.75,
                "&:hover": {
                  bgcolor: "transparent",
                  color: theme.primary,
                  textDecoration: "underline",
                },
              }}
            >
              Duplicate
            </Button>
          )}
          <Button
            component={Link}
            to={`/events/${event.id}`}
            variant="contained"
            disableElevation
            endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: "10px",
              bgcolor: theme.primary,
              color: "#fff",
              boxShadow: "none",
              px: 1.75,
              minHeight: 38,
              "&:hover": { bgcolor: theme.primaryHover || "#065f46", boxShadow: "none" },
            }}
          >
            View
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function DashboardEventsTab({
  events,
  loading,
  error,
  filter,
  onFilterChange,
  onRetry,
  canCreateEvent,
}) {
  const filteredEvents = useMemo(() => {
    const now = Date.now();
    return (events || []).filter((event) => {
      const start = new Date(event.start_datetime || 0).getTime();
      return filter === "past" ? start < now : start >= now;
    });
  }, [events, filter]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent={{ sm: "flex-end" }}
      >
        <Box
          role="group"
          aria-label="Show upcoming or past events"
          sx={{
            display: "flex",
            p: "4px",
            borderRadius: "12px",
            bgcolor: "#f1f5f9",
            border: "1px solid #e2e8f0",
            gap: "4px",
            alignSelf: { xs: "stretch", sm: "center" },
          }}
        >
          {[
            { id: "upcoming", label: "Upcoming" },
            { id: "past", label: "Past" },
          ].map(({ id, label }) => {
            const active = filter === id;
            return (
              <Button
                key={id}
                disableElevation
                onClick={() => onFilterChange(id)}
                sx={{
                  flex: 1,
                  py: 1,
                  px: id === "upcoming" ? 2.25 : 1.5,
                  minHeight: 40,
                  minWidth: 0,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  borderRadius: "10px",
                  boxShadow: active ? "0 1px 3px rgba(15, 23, 42, 0.08)" : "none",
                  bgcolor: active ? "#ffffff" : "transparent",
                  color: active ? theme.primary : "#64748b",
                  border: active ? `1px solid ${theme.primary}33` : "1px solid transparent",
                  "&:hover": {
                    bgcolor: active ? "#ffffff" : "rgba(255,255,255,0.6)",
                    color: active ? theme.primary : "#334155",
                  },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>

        {canCreateEvent && (
          <Button
            component={Link}
            to="/events/create"
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.875rem",
              lineHeight: 1.5,
              borderRadius: "10px",
              background: theme.gradient,
              "&:hover": { background: theme.gradient, opacity: 0.95 },
            }}
          >
            Create Event
          </Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
          <CircularProgress sx={{ color: theme.primary }} />
        </Box>
      ) : error ? (
        <Box sx={{ border: "1px solid #fecaca", borderRadius: 3, p: 3, backgroundColor: "#fff" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#991b1b" }}>
            Failed to load events
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: "#7f1d1d" }}>
            {error}
          </Typography>
          <Button onClick={onRetry} sx={{ mt: 2, textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
            Retry
          </Button>
        </Box>
      ) : filteredEvents.length === 0 ? (
        <Box
          sx={{
            border: "none",
            borderRadius: "12px",
            p: 4,
            textAlign: "center",
            backgroundColor: "#fff",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
            No {filter} events yet
          </Typography>
          {filter === "past" && (
            <Typography variant="body2" sx={{ mt: 1, color: "#0f172a" }}>
              Past hosted events will appear here after they end.
            </Typography>
          )}
          {canCreateEvent && filter === "upcoming" && (
            <Button
              component={Link}
              to="/events/create"
              variant="contained"
              sx={{
                mt: 2,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                background: theme.gradient,
                "&:hover": { background: theme.gradient, opacity: 0.95 },
              }}
            >
              Create Event
            </Button>
          )}
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredEvents.map((event) => (
            <EventManageCard key={event.id} event={event} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
